"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import * as amplitude from "@amplitude/unified";
import { isAmplitudeDisabled } from "@/lib/amplitude";
import { BAG_ENABLED } from "@/lib/comix/bagConfig";

// Build-time feature flags whose value changes what the user sees — logged once per session
// as "Feature Flag Evaluated" so experiments on them can be analysed (exposure = denominator).
const FEATURE_FLAGS: Record<string, boolean> = {
  comix_cast_review: process.env.NEXT_PUBLIC_COMIX_CAST_REVIEW === "true",
  comix_cast_photo_review: process.env.NEXT_PUBLIC_COMIX_CAST_PHOTO_REVIEW === "true",
  comix_photo_crop: process.env.NEXT_PUBLIC_FEATURE_PHOTO_CROP !== "false",
  comix_bag: BAG_ENABLED,
};

declare global {
  interface Window {
    __AMPLITUDE_INITIALIZED__?: boolean;
  }
}

const AMPLITUDE_API_KEY = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;

export default function Amplitude() {
  const pathname = usePathname();
  // prompt-playground is an internal dev/testing tool, not a real user-facing page —
  // skip analytics (and session replay, autocapture, etc.) there entirely rather than
  // mixing dev-testing noise into real product data.
  const isPromptPlayground = pathname?.startsWith("/prompt-playground") ?? false;

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!AMPLITUDE_API_KEY) return;
    if (isAmplitudeDisabled()) return; // local dev / localhost — keep out of real data
    if (isPromptPlayground) return;
    if (window.__AMPLITUDE_INITIALIZED__) return;

    window.__AMPLITUDE_INITIALIZED__ = true;
    void amplitude.initAll(AMPLITUDE_API_KEY, {
      analytics: { autocapture: true },
//       sessionReplay: { sampleRate: 1 },
    });

    for (const [flag, value] of Object.entries(FEATURE_FLAGS)) {
      void amplitude.track("Feature Flag Evaluated", { flag, value });
    }
    // Depends on isPromptPlayground (not []) so a session that STARTS on
    // /prompt-playground still initializes once it navigates to a real page, instead of
    // never initializing for the rest of the session — window.__AMPLITUDE_INITIALIZED__
    // above keeps this idempotent on every later re-run.
  }, [isPromptPlayground]);

  // Page views come from Amplitude autocapture (`analytics.autocapture: true` above), which
  // tracks SPA route changes and carries full URL / referrer / title. A second manual
  // "Page Viewed" here only duplicated that with less data, so it was removed — use the
  // autocapture "[Amplitude] Page Viewed" event for page-view analysis.

  return null;
}
